import { Component, inject, NgZone, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core';
import { AuthService } from "./modules/authentication/services/auth.service";
import { NavigationStart, Router, RouterModule, RouterOutlet } from "@angular/router";
import { Location, NgOptimizedImage } from '@angular/common';
import { environment } from "../environment";
import { FEATURES } from "./modules/shared/config/feature-flags";
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { SocketService } from "./modules/shared/services/socket.service";
import { HttpRequestTrackerService } from "./modules/shared/services/http-request-tracker.service";
import { animate, style, transition, trigger } from "@angular/animations";
import { TokenRefreshService } from "./modules/authentication/services/token-refresh.service";
import { KeycloakService } from "keycloak-angular";
import { Subscription, take } from "rxjs";
import { ToastrService, type IndividualConfig } from "ngx-toastr";
import { MatomoService, resolveMatomoSiteId } from "./modules/shared/services/matomo.service";
import { isPublicRoute } from "./public-routes.config";
import { DashboardModalComponent } from "./modules/admin/components/dashboard-modal/dashboard-modal.component";
import { SeasonalAnimationComponent } from "./modules/shared/components/seasonal-animation/seasonal-animation.component";
import { SeasonalThemePopupComponent } from "./modules/shared/components/seasonal-theme-popup/seasonal-theme-popup.component";
import { MessageriePreloadService } from "./modules/messagerie/services/messagerie-preload.service";
import { DailyDeclarationService } from "./modules/sirh/services/daily-declaration.service";
import { DailyDeclarationModalComponent } from "./modules/sirh/components/daily-declaration-modal/daily-declaration-modal.component";
import { ModalService } from "./modules/shared/services/modal.service";
import { DialogAutoFocusService } from "./modules/shared/services/dialog-auto-focus.service";
import { DarkModeService } from "./modules/shared/services/dark-mode.service";
import { FontService } from "./modules/shared/services/font.service";
import { CursorService } from "./modules/shared/services/cursor.service";
import { ZoomService } from "./modules/shared/services/zoom.service";
import { UtilisateurService } from "./modules/authentication/services/utilisateur.service";
import { AppUpdateService } from "./modules/shared/services/app-update.service";
import type { User } from "./modules/authentication/models/user";
import type { RcToastPayload } from "./modules/shared/components/rc-toast/rc-toast.model";
import { ApplicationReadinessService } from './modules/shared/services/application-readiness.service';
import { VisualPerformanceService } from './modules/shared/services/visual-performance.service';
import { AchievementToastComponent } from './modules/shared/achievements/achievement-toast.component';
import { AchievementBus } from './modules/shared/achievements/achievement-bus';
import { KonamiService } from './modules/shared/services/konami.service';
import { KonamiComponent } from './modules/shared/components/konami/konami.component';
import { KeyboardShortcutsService } from './modules/admin/services/keyboard-shortcuts.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { ModalDashboardService } from './modules/admin/services/modal-dashboard.service';

type LoadingBrandId = 'rc' | 'alg';

interface LoadingBrand {
    id: LoadingBrandId;
    name: string;
    logo: string;
    logoWhite: string;
    width: number;
    height: number;
    whiteHeight: number;
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.css',
    imports: [
        LoadingBarModule,
        RouterModule,
        RouterOutlet,
        NgOptimizedImage,
        DashboardModalComponent,
        SeasonalAnimationComponent,
        SeasonalThemePopupComponent,
        DailyDeclarationModalComponent,
        AchievementToastComponent,
        KonamiComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    animations: [
        trigger('loadingScreenTransition', [
            transition(':enter', [
                style({ opacity: 0, transform: 'scale(1.006)', filter: 'blur(6px)' }),
                animate('280ms cubic-bezier(0.22, 1, 0.36, 1)', style({
                    opacity: 1,
                    transform: 'scale(1)',
                    filter: 'blur(0)',
                }))
            ]),
            transition(':leave', [
                style({ opacity: 1, transform: 'scale(1)', filter: 'blur(0)' }),
                animate('640ms cubic-bezier(0.22, 1, 0.36, 1)', style({
                    opacity: 0,
                    transform: 'scale(1.012)',
                    filter: 'blur(10px)',
                }))
            ]),
        ])
    ]
})
export class AppComponent implements OnInit, OnDestroy {
    authService = inject(AuthService);
    private socketService = inject(SocketService);
    private location = inject(Location);
    private router = inject(Router);
    private requestTracker = inject(HttpRequestTrackerService);
    private ngZone = inject(NgZone);
    private keycloakService = inject(KeycloakService);
    private tokenRefreshService = inject(TokenRefreshService);
    private matomoService = inject(MatomoService);
    private messageriePreloadService = inject(MessageriePreloadService);
    private dailyDeclarationService = inject(DailyDeclarationService);
    private modalService = inject(ModalService);
    private darkModeService = inject(DarkModeService);
    private toastrService = inject(ToastrService);
    private utilisateurService = inject(UtilisateurService);
    private appUpdateService = inject(AppUpdateService);
    private readonly applicationReadiness = inject(ApplicationReadinessService);
    private dialogAutoFocusService = inject(DialogAutoFocusService);
    private achievementBus = inject(AchievementBus);
    protected readonly konami = inject(KonamiService);
    private keyboardShortcutsService = inject(KeyboardShortcutsService);
    protected readonly dashboardModalOpen = toSignal(inject(ModalDashboardService).isModalOpen$, { initialValue: false });
    protected currentRouteIsPublic = isPublicRoute(window.location.pathname);

    keycloak = environment.keycloak;
    /** Plateforme sans succès : le bandeau de déblocage n'est pas monté. */
    readonly achievementsEnabled = FEATURES.ACHIEVEMENTS;
    path: string;
    waiting = false;
    protected readonly loadingBrand = this.resolveLoadingBrand();
    protected readonly appLoadingScreenVisible = signal(!this.canRenderAppContent);
    protected readonly appContentRevealed = this.applicationReadiness.contentReady;
    private lastUrl: string = '';
    private lastActivityTime: number;
    private currentUserStatus: string = 'EN_LIGNE';
    private currentUserPreferredStatus: string = 'EN_LIGNE';
    private readonly INACTIVITY_LIMIT = 900000; // 15 minutes
    private userSubscription: Subscription;
    private inactivityCheckInterval: number | null = null;
    private activityListenersRegistered = false;
    private activityThrottleTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly activityListener = (): void => {
        if (this.activityThrottleTimer !== null) {
            return;
        }

        this.activityDetected();
        this.activityThrottleTimer = setTimeout(() => {
            this.activityThrottleTimer = null;
        }, 1000);
    };
    private emailReminderNoticeToastId: number | null = null;
    private emailReminderNoticeHandled = false;
    private appLoadingScreenHideTimeout: ReturnType<typeof setTimeout> | null = null;
    private readonly APP_LOADING_SCREEN_EXIT_DELAY_MS = 320;

    constructor() {
        inject(VisualPerformanceService);
        this.applicationReadiness.setContentReady(this.canRenderAppContent);
        // Instancie les services ici pour appliquer la police/le zoom sauvegardés
        // dès le démarrage (et, pour le zoom, brancher l'interception Ctrl+molette
        // / Ctrl+/- dès le début, pas seulement une fois le panneau réglages ouvert).
        inject(FontService);
        inject(CursorService);
        inject(ZoomService);
    }

    ngOnInit(): void {
        if (environment.MAINTENANCE) {
            return;
        }

        this.appUpdateService.initialize();
        this.dialogAutoFocusService.start();
        this.path = this.location.path();
        this.updateAppLoadingScreenVisibility();

        this.startMatomo();

        if (!this.currentRouteIsPublic) {
            this.socketService.connect('chat');
            this.socketService.connect('notification');
        }

        this.router.events.subscribe(event => {
            this.lastUrl = this.location.path().split('?')[0];
            if (event instanceof NavigationStart && event.url.split('?')[0] !== this.lastUrl) {
                this.requestTracker.cancelPendingRequests();
            }
        });

        this.darkModeService.applyCurrentPreferences();

        if (!this.currentRouteIsPublic) {
            let init = true;
            this.userSubscription = this.authService.user.subscribe(user => {
                this.updateAppLoadingScreenVisibility();

                if (user) {
                    this.initTokenRefreshService();
                    this.setupActivityListeners();
                    this.startInactivityCheck();
                    this.currentUserStatus = user.status?.status || 'EN_LIGNE';
                    this.currentUserPreferredStatus = user.status?.preferred_status || user.status?.status || 'EN_LIGNE';
                    this.showEmailReminderNoticeIfNeeded(user);

                    if (init) {
                        init = false;
                        if (this.canAutoCyclePresence()) {
                            this.sendAutomaticUserStatus('EN_LIGNE', true);
                        }

                        // Précharger les données de messagerie dès l'authentification
                        // pour qu'elles soient prêtes quand l'utilisateur ouvre la messagerie
                        this.messageriePreloadService.preload();

                        // Vérifier si la déclaration SIRH quotidienne est requise
                        this.checkDailyDeclaration();

                        // Rattache la visite Matomo et libère les pages vues retenues.
                        this.matomoService.identify(user);

                        // Succès « Raccourci » : le premier raccourci suffit, quel qu'il soit.
                        this.keyboardShortcutsService.getShortcutTriggered$().pipe(take(1)).subscribe({
                            next: () => this.achievementBus.unlock('navigation.raccourci-clavier'),
                        });
                    }
                }
            });
        }
    }

    ngOnDestroy(): void {
        if (this.userSubscription) {
            this.userSubscription.unsubscribe();
        }
        if (this.inactivityCheckInterval !== null) {
            clearInterval(this.inactivityCheckInterval);
        }
        if (this.activityListenersRegistered) {
            document.removeEventListener('mousemove', this.activityListener);
            document.removeEventListener('keydown', this.activityListener);
            document.removeEventListener('scroll', this.activityListener);
            document.removeEventListener('click', this.activityListener);
            this.activityListenersRegistered = false;
        }
        if (this.activityThrottleTimer !== null) {
            clearTimeout(this.activityThrottleTimer);
            this.activityThrottleTimer = null;
        }
        this.clearAppLoadingScreenHideTimeout();
        this.tokenRefreshService.stopTokenRefreshMonitoring();
    }

    /**
     * Démarre Matomo avant toute navigation pour ne pas perdre la première page.
     * Routes publiques : destinataires externes sans compte, mesurés sans cookie.
     */
    private startMatomo(): void {
        const siteId = resolveMatomoSiteId();
        if (siteId) {
            this.matomoService.start({ siteId, anonymous: this.currentRouteIsPublic });
        }
    }

    /**
     * Initialise le service de rafraîchissement de token
     */
    private async initTokenRefreshService(): Promise<void> {
        try {
            if (await this.keycloakService.isLoggedIn()) {
                this.tokenRefreshService.startTokenRefreshMonitoring();
                this.tokenRefreshService.getTokenExpirationTime();
            }
        } catch (error) {
            console.error('Erreur lors de l\'initialisation du service de rafraîchissement de token', error);
        }
    }

    private setupActivityListeners() {
        if (this.activityListenersRegistered) {
            return;
        }

        this.activityListenersRegistered = true;
        this.ngZone.runOutsideAngular(() => {
            document.addEventListener('mousemove', this.activityListener, { passive: true });
            document.addEventListener('keydown', this.activityListener, { passive: true });
            document.addEventListener('scroll', this.activityListener, { passive: true });
            document.addEventListener('click', this.activityListener, { passive: true });
        });
    }

    private activityDetected() {
        this.lastActivityTime = Date.now();

        if (this.canAutoCyclePresence()) {
            this.sendAutomaticUserStatus('EN_LIGNE');
        }
    }

    private startInactivityCheck() {
        if (this.inactivityCheckInterval !== null) {
            return;
        }

        this.lastActivityTime = Date.now();
        this.inactivityCheckInterval = setInterval(() => {
            const currentTime = Date.now();
            if (currentTime - this.lastActivityTime > this.INACTIVITY_LIMIT && this.canAutoCyclePresence()) {
                this.sendAutomaticUserStatus('ABSENT');
            }
        }, this.INACTIVITY_LIMIT / 2) as unknown as number;
    }

    private canAutoCyclePresence(): boolean {
        const userStatus = this.authService.userSignal()?.status;
        const preferredStatus = userStatus?.preferred_status || this.currentUserPreferredStatus || 'EN_LIGNE';
        return preferredStatus === 'EN_LIGNE';
    }

    private showEmailReminderNoticeIfNeeded(user: User): void {
        const emailReminders = user.config?.messagerie?.emailReminders ?? {};
        if (
            this.emailReminderNoticeToastId !== null ||
            this.emailReminderNoticeHandled ||
            emailReminders.noticeDismissed === true ||
            emailReminders.enabled === false
        ) {
            return;
        }

        const toastOptions: Partial<IndividualConfig<RcToastPayload>> = {
            disableTimeOut: true,
            tapToDismiss: false,
            closeButton: false,
            toastClass: 'ngx-toastr messagerie-email-reminder-toast',
            payload: {
                variant: 'info',
                iconClass: 'fa-solid fa-envelope',
                description: 'Ces rappels sont activés par défaut. Si un message privé ou de groupe reste non lu pendant un moment, un mail de rappel peut vous être envoyé. Vous pouvez activer ou désactiver ces rappels à tout moment dans les paramètres de la messagerie.',
                actionsLayout: 'split',
                actions: [
                    {
                        id: 'disable-email-reminders',
                        label: 'Désactiver maintenant',
                        ariaLabel: 'Désactiver maintenant les rappels par mail de la messagerie',
                        variant: 'secondary',
                        callback: () => this.disableEmailRemindersFromNotice(),
                    },
                    {
                        id: 'confirm-email-reminders-notice',
                        label: 'Confirmer',
                        ariaLabel: 'Confirmer la lecture de la mise à jour sur les rappels mail des messages non lus',
                        variant: 'primary',
                        confirmOnHoverSeconds: 3,
                        callback: () => this.dismissEmailReminderNotice(),
                    },
                ],
            },
        };

        const toast = this.toastrService.info(
            'Les rappels par mail des messages non lus sont maintenant activés par défaut dans la messagerie.',
            'Rappels par mail des messages non lus',
            toastOptions,
        );

        if (!toast) {
            return;
        }

        this.emailReminderNoticeToastId = toast.toastId;
        toast.onHidden.pipe(take(1)).subscribe(() => {
            this.emailReminderNoticeToastId = null;
            // On ne mémorise PAS ici : un masquage non explicite (navigation, reload,
            // clear programmatique) doit laisser la notice réapparaître. Seuls les
            // boutons « Confirmer » et « Désactiver » posent noticeDismissed.
        });
    }

    private dismissEmailReminderNotice(): void {
        this.emailReminderNoticeHandled = true;
        this.saveEmailReminderNoticeConfig({ noticeDismissed: true });
    }

    private disableEmailRemindersFromNotice(): void {
        this.emailReminderNoticeHandled = true;
        this.saveEmailReminderNoticeConfig({
            enabled: false,
            noticeDismissed: true,
        });
    }

    private saveEmailReminderNoticeConfig(emailReminders: Record<string, boolean>): void {
        this.utilisateurService.editUserConfig({
            messagerie: {
                emailReminders,
            },
        });
    }

    private sendAutomaticUserStatus(status: string, force: boolean = false) {
        if (this.currentUserStatus !== status || force) {
            const user = this.authService.userSignal();
            if (!user?.id) {
                return;
            }

            this.currentUserStatus = status;
            this.socketService.sendMessage('chat', {
                type: 'update.user.status',
                user_id: user.id,
                status: status,
                manual: false
            });
            let userData = { ...user };
            userData.status = {
                ...userData.status,
                status: status,
                preferred_status: userData.status?.preferred_status || this.currentUserPreferredStatus || 'EN_LIGNE',
                last_activity: new Date().toISOString()
            };
            this.authService.user.next(userData);
        }
    }

    /**
     * Vérifie si l'utilisateur doit remplir sa déclaration SIRH du jour
     */
    private checkDailyDeclaration(): void {
        // Vérifier si déjà dismissé aujourd'hui
        if (this.dailyDeclarationService.checkDismissedToday()) {
            return;
        }

        // Vérifier si l'utilisateur a accès au module SIRH
        const apps = this.authService.getApps();
        if (!apps || !apps.includes('SIRH-DECLARATIONS')) {
            return;
        }

        // Appeler l'API pour vérifier si la déclaration est requise
        this.dailyDeclarationService.checkDeclarationRequired().subscribe({
            next: (result) => {
                if (result.required) {
                    // La modale a son propre skeleton de chargement (isLoadingWeek) : pas besoin
                    // d'un délai artificiel supplémentaire avant de l'ouvrir, ça ne fait que retarder
                    // l'affichage sans raison.
                    this.modalService.open('daily-declaration-modal');
                }
            },
            error: (err) => {
                console.error('Erreur lors de la vérification de la déclaration SIRH:', err);
            }
        });
    }

    protected readonly environment = environment;

    protected get canRenderAppContent(): boolean {
        return Boolean(
            (this.keycloak && this.authService.userSignal())
            || !this.keycloak
            || this.path === '/erreur'
            || environment.MAINTENANCE
            || this.currentRouteIsPublic
        );
    }

    protected get loadingScreenVisible(): boolean {
        return this.appLoadingScreenVisible();
    }

    protected get loadingBrandLogo(): string {
        return localStorage.theme === 'dark' ? this.loadingBrand.logoWhite : this.loadingBrand.logo;
    }

    protected get loadingBrandLogoHeight(): number {
        return localStorage.theme === 'dark' ? this.loadingBrand.whiteHeight : this.loadingBrand.height;
    }

    private resolveLoadingBrand(): LoadingBrand {
        const hostname = window.location.hostname.toLowerCase();
        const baseUrlHostname = this.getEnvironmentBaseUrlHostname();
        const rcAdminHosts = new Set([
            'rc-global.fr',
            'admin.reseau-concept.com',
            'admin-dev.reseau-concept.com',
            'admin-dev.reseau-concept.net',
        ]);
        const isRcAdmin = rcAdminHosts.has(hostname) || rcAdminHosts.has(baseUrlHostname);
        const isAlgGlobal = environment.title.toUpperCase().includes('ALG');

        if (!isRcAdmin && isAlgGlobal) {
            return {
                id: 'alg',
                name: 'ALG Global',
                logo: 'assets/brands/alg-global/logo/logo_alg_global.png',
                logoWhite: 'assets/brands/alg-global/logo/logo_alg_global_white.png',
                width: 494,
                height: 471,
                whiteHeight: 473,
            };
        }

        return {
            id: 'rc',
            name: 'Réseau Concept',
            logo: 'assets/brands/rc-global/logo/RC_logo_couleurs.png',
            logoWhite: 'assets/brands/rc-global/logo/RC_logo_blanc.png',
            width: 632,
            height: 666,
            whiteHeight: 666,
        };
    }

    private getEnvironmentBaseUrlHostname(): string {
        try {
            return new URL(environment.BASE_URL).hostname.toLowerCase();
        } catch {
            return '';
        }
    }

    private updateAppLoadingScreenVisibility(): void {
        if (!this.canRenderAppContent) {
            this.clearAppLoadingScreenHideTimeout();
            this.appLoadingScreenVisible.set(true);
            this.applicationReadiness.setContentReady(false);
            return;
        }

        if (!this.appLoadingScreenVisible()) {
            this.applicationReadiness.setContentReady(true);
            return;
        }

        this.clearAppLoadingScreenHideTimeout();
        this.appLoadingScreenHideTimeout = setTimeout(() => {
            this.applicationReadiness.setContentReady(true);
            this.appLoadingScreenVisible.set(false);
            this.appLoadingScreenHideTimeout = null;
        }, this.APP_LOADING_SCREEN_EXIT_DELAY_MS);
    }

    private clearAppLoadingScreenHideTimeout(): void {
        if (this.appLoadingScreenHideTimeout === null) {
            return;
        }

        clearTimeout(this.appLoadingScreenHideTimeout);
        this.appLoadingScreenHideTimeout = null;
    }
}
